'use client';

import { useLayoutEffect, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { Box } from '@terra/ui-styled-system';
import { IconButton, Text, Tooltip } from '@terra/ui-design-system';
import { ImportantBookIcon } from '@terra/ui-icons';
import { CoPilotChat } from '../CoPilotChat';
import { MainContentTabPanel } from './MainContentTabPanel';
import { TabScopeProvider } from '../../contexts/TabScopeContext';
import { useLayoutStore } from '../../store/layoutStore';
import { useTargetId, useTargetType } from '../../store/projectStore';
import { buildTabScopeKey } from '../../store/tab/tab-scope-key';
import {
  resolveScopedActiveChatTabId,
  useChatTabsStore,
} from '../../store/chatTabsStore';
import { useDetachedWindows } from '../../hooks/useDetachedWindows';
import { useCrossWindowSyncReady } from '../../hooks/useCrossWindowSyncReady';
import type { DetachedWindowMode } from '../../utils/detachedMode';
import { DetachedWindowDragBar } from './DetachedWindowDragBar';
import { ErrorBoundary } from '../ErrorBoundary';
import { resolveOverviewTabTitle } from './utils';
import { switchToChatTab } from '../chat/actions/chatActions';

type DetachedTabLayoutProps = {
  mode: DetachedWindowMode;
};

function ReattachIconButton({
  onReattach,
  label,
  iconSize = 14,
}: {
  onReattach: () => void;
  label: string;
  iconSize?: number;
}) {
  return (
    <Tooltip title={label} positioning={{ placement: 'bottom' }}>
      <IconButton
        variant="link"
        size="xs"
        onClick={onReattach}
        aria-label={label}
        color="fg.muted"
        _hover={{ color: 'fg.default', bg: 'transparent' }}
        minW="auto"
        h="auto"
        p="1"
      >
        <ImportantBookIcon size={iconSize} />
      </IconButton>
    </Tooltip>
  );
}

function DetachedTabHeader({
  title,
  onReattach,
  reattachLabel,
}: {
  title: string;
  onReattach: () => void;
  reattachLabel: string;
}) {
  return (
    <Box
      h="8"
      px="2"
      borderBottomWidth="1px"
      borderColor="border.subtle"
      bg="bg.canvas"
      flexShrink={0}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      gap="2"
    >
      <Text
        flex="1"
        minW="0"
        fontSize="sm"
        fontWeight="medium"
        color="fg.default"
        truncate
      >
        {title}
      </Text>
      <ReattachIconButton
        onReattach={onReattach}
        label={reattachLabel}
        iconSize={12}
      />
    </Box>
  );
}

function DetachedMainTabContent({ tabId }: { tabId: string }) {
  const isSyncReady = useCrossWindowSyncReady();
  const targetId = useTargetId();
  const targetType = useTargetType();
  const tabs = useLayoutStore((state) => state.mainContentTabs);
  const tabNavigationByTabId = useLayoutStore(
    (state) => state.tabNavigationByTabId,
  );
  const { reattachTab } = useDetachedWindows();
  const t = useTranslations('proxy.detachedWindows');
  const tLayout = useTranslations('layout');
  const tab = tabs.find((entry) => entry.id === tabId);
  const displayTitle = tab
    ? resolveOverviewTabTitle({
        tabId: tab.id,
        title: tab.title,
        tabType: tab.type,
        targetType,
        home: tLayout('home'),
        targetOverview: tLayout('targetOverview'),
      })
    : '';
  const activePageEntry = useMemo(() => {
    const navigation = tabNavigationByTabId[tabId];

    if (!navigation) {
      return undefined;
    }

    return navigation.entries[navigation.currentIndex];
  }, [tabId, tabNavigationByTabId]);
  const activeTabScopeId = useMemo(() => {
    if (!tab) {
      return undefined;
    }

    return buildTabScopeKey({
      targetId,
      tabId: tab.id,
      pageKey: activePageEntry?.pageId ?? tab.id,
    });
  }, [activePageEntry?.pageId, targetId, tab]);

  if (!isSyncReady) {
    return (
      <Box
        h="100vh"
        w="100vw"
        display="flex"
        flexDirection="column"
        bg="bg.canvas"
      >
        <DetachedWindowDragBar />
      </Box>
    );
  }

  if (!tab) {
    return (
      <Box
        h="100vh"
        w="100vw"
        display="flex"
        flexDirection="column"
        bg="bg.canvas"
      >
        <DetachedWindowDragBar />
        <VStackCentered message={t('tabNotFound')} />
      </Box>
    );
  }

  return (
    <Box
      h="100vh"
      w="100vw"
      display="flex"
      flexDirection="column"
      bg="bg.canvas"
    >
      <DetachedWindowDragBar />
      <DetachedTabHeader
        title={displayTitle}
        onReattach={() => void reattachTab(tabId)}
        reattachLabel={t('reattachTab')}
      />
      <TabScopeProvider scopeId={activeTabScopeId}>
        <Box flex="1" overflow="hidden" minH="0">
          <ErrorBoundary key={tab.id} scopeId={tab.id}>
            <MainContentTabPanel tab={tab} activePageEntry={activePageEntry} />
          </ErrorBoundary>
        </Box>
      </TabScopeProvider>
    </Box>
  );
}

function VStackCentered({ message }: { message: string }) {
  return (
    <Box
      flex="1"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg="bg.canvas"
    >
      <Text color="fg.muted">{message}</Text>
    </Box>
  );
}

function DetachedCopilotContent() {
  const isSyncReady = useCrossWindowSyncReady();
  const targetId = useTargetId();
  const targetType = useTargetType();
  const chatTabs = useChatTabsStore((state) => state.chatTabs);
  const activeChatTabId = useChatTabsStore((state) => state.activeChatTabId);

  useLayoutEffect(() => {
    if (!isSyncReady || !targetId) {
      return;
    }

    const nextActiveChatTabId = resolveScopedActiveChatTabId({
      chatTabs,
      activeChatTabId,
      targetId,
      targetType,
    });

    if (nextActiveChatTabId && nextActiveChatTabId !== activeChatTabId) {
      switchToChatTab({ tabId: nextActiveChatTabId });
    }
  }, [isSyncReady, targetId, targetType, chatTabs, activeChatTabId]);

  return (
    <Box
      h="100vh"
      w="100vw"
      minW="580px"
      display="flex"
      flexDirection="column"
      bg="bg.canvas"
      overflow="hidden"
    >
      <DetachedWindowDragBar />
      <Box flex="1" overflow="hidden" minH="0" minW="0">
        {isSyncReady ? <CoPilotChat key={targetId || 'no-target'} /> : null}
      </Box>
    </Box>
  );
}

export function DetachedTabLayout({ mode }: DetachedTabLayoutProps) {
  if (mode.type === 'tab') {
    return <DetachedMainTabContent tabId={mode.tabId} />;
  }

  if (mode.type === 'copilot') {
    return <DetachedCopilotContent />;
  }

  return null;
}
